import { Button, ConfirmSheet, Input, PageHeader, StatusChip } from "@jod/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { OwnerDataTable as DataTable } from "../../components/owner-data-table.tsx";
import {
  useCreateTable,
  useDeactivateTable,
  useOwnerState,
  useUpdateTable,
} from "../../features/backoffice/queries.ts";
import type { TableConfig } from "../../features/backoffice/types.ts";

export const Route = createFileRoute("/_app/tables")({ component: TablesScreen });

function TablesScreen() {
  const { data } = useOwnerState();
  const create = useCreateTable();
  const update = useUpdateTable();
  const deactivate = useDeactivateTable();
  const [pendingDeactivate, setPendingDeactivate] = useState<TableConfig | null>(null);
  const zones = data?.zones ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tables"
        subtitle="CRUD table codes, capacity, zone assignment, order-only, and stale QR warnings."
        actions={
          <Button
            onClick={() =>
              create.mutate({ label: "Bulk Table", code: `T${(data?.tables.length ?? 0) + 1}` })
            }
          >
            Create table
          </Button>
        }
      />
      {deactivate.error ? (
        <p role="alert" className="rounded-md bg-destructive/10 p-3 text-destructive text-sm">
          {deactivate.error instanceof Error
            ? deactivate.error.message
            : "Could not deactivate table."}
        </p>
      ) : null}
      <DataTable
        rows={data?.tables ?? []}
        getRowKey={(row) => row.id}
        columns={[
          {
            key: "code",
            header: "Code",
            cell: (row) => (
              <Input
                aria-label={`${row.code} code`}
                defaultValue={row.code}
                onBlur={(event) =>
                  event.target.value !== row.code &&
                  update.mutate({ tableId: row.id, input: { code: event.target.value } })
                }
              />
            ),
          },
          {
            key: "label",
            header: "Label",
            cell: (row) => (
              <Input
                aria-label={`${row.code} label`}
                defaultValue={row.label}
                onBlur={(event) =>
                  event.target.value !== row.label &&
                  update.mutate({ tableId: row.id, input: { label: event.target.value } })
                }
              />
            ),
          },
          {
            key: "zone",
            header: "Zone",
            cell: (row) => (
              <select
                aria-label={`${row.code} zone`}
                value={row.zoneId}
                onChange={(event) =>
                  update.mutate({ tableId: row.id, input: { zoneId: event.target.value } })
                }
                className="flex h-11 w-full rounded-md border border-input bg-transparent px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {zones.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    {zone.name}
                  </option>
                ))}
              </select>
            ),
          },
          {
            key: "capacity",
            header: "Capacity",
            cell: (row) => (
              <Input
                aria-label={`${row.code} capacity`}
                type="number"
                min={1}
                defaultValue={row.capacity}
                onBlur={(event) =>
                  Number(event.target.value) !== row.capacity &&
                  update.mutate({
                    tableId: row.id,
                    input: { capacity: Number(event.target.value) },
                  })
                }
              />
            ),
          },
          {
            key: "status",
            header: "Status",
            cell: (row) => (
              <StatusChip
                tone={
                  row.status === "occupied"
                    ? "warning"
                    : row.status === "free"
                      ? "success"
                      : row.status === "disabled"
                        ? "neutral"
                        : "neutral"
                }
              >
                {row.status}
              </StatusChip>
            ),
          },
          {
            key: "social",
            header: "Mode",
            cell: (row) => (
              <Button
                size="sm"
                variant="outline"
                className="text-foreground"
                onClick={() =>
                  update.mutate({ tableId: row.id, input: { orderOnly: !row.orderOnly } })
                }
              >
                {row.orderOnly ? "non-social/order-only" : "social allowed"}
              </Button>
            ),
          },
          {
            key: "qr",
            header: "QR",
            cell: (row) =>
              row.staleQrWarning ? <StatusChip tone="warning">stale QR</StatusChip> : "current",
          },
          {
            key: "actions",
            header: "Actions",
            cell: (row) => (
              <Button
                size="sm"
                variant="destructive"
                disabled={row.status === "disabled"}
                onClick={() => setPendingDeactivate(row)}
              >
                {row.status === "disabled" ? "Deactivated" : "Deactivate"}
              </Button>
            ),
          },
        ]}
      />
      <ConfirmSheet
        open={pendingDeactivate !== null}
        onOpenChange={(open) => !open && setPendingDeactivate(null)}
        title={`Deactivate table ${pendingDeactivate?.label ?? ""}?`}
        description="The table is marked disabled and its QR stops resolving new check-ins. Existing check-in/order history is kept. Rejected while a check-in is still active at the table."
        confirmLabel="Deactivate table"
        destructive
        onConfirm={() => pendingDeactivate && deactivate.mutate(pendingDeactivate.id)}
      />
    </div>
  );
}
